import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, map, retry, retryWhen, switchMap, take, tap } from 'rxjs/operators';
import { LanguageService } from '../language/language.service';
import { IPrinter } from '../printer/models/printer.model';
import { IQueue } from '../printer/models/queue.model';
import { ITenant } from '../tenant/models/tenant.model';
import { IUser } from './models/user.model';
import { StorageService } from '../storage/storage.service';
import { ITenantLite } from '../tenant/models/tenant-lite.model';

@Injectable({
  providedIn: 'root'
})

export class UserService {

  public userSignal = signal<IUser | null>(null);
  private userRaw: any = null;
  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private languageService: LanguageService,
    private loggingService: LoggingService,
    private storageService: StorageService
  ) {
    this.logger = this.loggingService.getLogger("[UserService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  private deserializeLinkObjects(user: IUser, links: any): void {
    if (links) {
      if (links['self'] && links['self'].href) {
        user.links.self = links['self'].href;
      }
      if (links['px:printjobs'] && links['px:printjobs'].href) {
        user.links.printJobs = links['px:printjobs'].href;
      }
      if (links['px:idCode'] && links['px:idCode'].href) {
        user.links.idCode = links['px:idCode'].href;
      }
      if (links['px:pincode'] && links['px:pincode'].href) {
        user.links.pinCode = links['px:pincode'].href;
      }
      if (links['px:cards'] && links['px:cards'].href) {
        user.links.cards = links['px:cards'].href;
      }
      if (links['px:workflows'] && links['px:workflows'].href) {
        user.links.workflows = links['px:workflows'].href;
      }
      if (links['px:releaseQueues'] && links['px:releaseQueues'].href) {
        user.links.releaseQueues = links['px:releaseQueues'].href;
      }
    }
  }

  private deserializeEmbeddedObjects(user: IUser, embedded: any): void {
    if (embedded) {
      user.embedded.authentication_details.source = embedded.authentication_details.source ? embedded.authentication_details.source : null;
      user.embedded.permissions.permissions = embedded.permissions.permissions ? embedded.permissions.permissions : null;
      user.embedded.permissions.role = embedded.permissions.role ? embedded.permissions.role : null;
      user.embedded.authentication_details.source = embedded.authentication_details.source ? embedded.authentication_details.source : null;
      user.embedded.authentication_details.principal = embedded.authentication_details.principal ? embedded.authentication_details.principal : null;
      user.embedded.user.links.self.href = embedded.user._links.self.href ? embedded.user._links.self.href : null;
      user.embedded.user.email = embedded.user.email ? embedded.user.email : null;
      user.embedded.user.name = embedded.user.name ? embedded.user.name : null;
      user.embedded.user.language = embedded.user.language ? embedded.user.language : null;
    }
  }

  private deserializeUserAppSettings(user: IUser, userAppSettings: any): void {
    if (userAppSettings) {
      user.userAppSettings.lastUsedPrinter = userAppSettings.lastUsedPrinter ? userAppSettings.lastUsedPrinter : null;
      user.userAppSettings.lastUsedQueue = userAppSettings.lastUsedQueue ? userAppSettings.lastUsedQueue : null;
      user.userAppSettings.lastUsedNetwork = userAppSettings.lastUsedNetwork ? userAppSettings.lastUsedNetwork : null;
      user.userAppSettings.lastUsedTenant = userAppSettings.lastUsedTenant ? userAppSettings.lastUsedTenant : null;
      user.userAppSettings.changeLogReviewDate = userAppSettings.changeLogReviewDate ? userAppSettings.changeLogReviewDate : null;
      user.userAppSettings.pinnedPrinterQueues = userAppSettings.pinnedPrinterQueues ? userAppSettings.pinnedPrinterQueues : null;
      user.userAppSettings.pinnedPrinters = userAppSettings.pinnedPrinters ? userAppSettings.pinnedPrinters : null;
      user.userAppSettings.securePrintMethods = userAppSettings.securePrintMethods ? userAppSettings.securePrintMethods : null;
      user.userAppSettings.serviceMenuEnabled = userAppSettings.serviceMenuEnabled ? userAppSettings.serviceMenuEnabled : null;
    }
  }

  private deserializeUser(input: any, skipObservers: boolean): IUser {
    let user: IUser = {
      links: {
        self: null,
        printJobs: null,
        idCode: null,
        pinCode: null,
        cards: null,
        releaseQueues: null,
        workflows: null
      },
      embedded: {
        permissions: {
          permissions: null,
          role: null
        },
        authentication_details: {
          source: null,
          principal: null
        },
        user: {
          links: {
            self: {
              href: null
            }
          },
          email: null,
          name: null,
          language: null
        }
      },
      groups: input._embedded.groups ? this.formatGroupsArray(input._embedded.groups) : [],
      userAppSettings: {
        lastUsedPrinter: null,
        lastUsedQueue: null,
        lastUsedNetwork: null,
        lastUsedTenant: null,
        changeLogReviewDate: null,
        pinnedPrinterQueues: null,
        pinnedPrinters: null,
        securePrintMethods: null,
        serviceMenuEnabled: null
      },
      name: input._embedded.user.name ? input._embedded.user.name : null,
      inactivityAutoDelete: input.inactivityAutoDelete ? input.inactivityAutoDelete : false,
      api: {
        getIdCode: (user: IUser) => {
          return this.getIdCode(user);
        },
        setIdCode: (tenant: ITenant) => {
          return this.setIdCode(tenant);
        },
        deleteIdCode: (idCode:any) => {
          return this.deleteIdCode(idCode);
        },
        getPinCode: (user: IUser) => {
          return this.getPinCode(user);
        },
        setPinCode: (user: IUser, pinCode: any) => {
          return this.setPinCode(user, pinCode);
        },
        deletePinCode: (user: IUser) => {
          return this.deletePinCode(user);
        },
        getCards: (user: IUser) => {
          return this.getCards(user);
        },
        deleteCard: (card: any) => {
          return this.deleteCard(card);
        },
        registerCard: (user: IUser, cardCode: any) => {
          return this.registerCard(user, cardCode);
        }
      }
    };
    if (input._links) {this.deserializeLinkObjects(user, input._links);}
    if (input._embedded) {this.deserializeEmbeddedObjects(user, input._embedded);}
    if (input.userAppSettings) {this.deserializeUserAppSettings(user, input.userAppSettings);}
    if (!skipObservers) {
      this.languageService.changeLanguage(user.embedded.user.language);
    }
    return user;
  }

  private formatGroupsArray (groupsArrayRaw): Array<string> {
    let groupsArray: Array<string> = [];
    if (groupsArrayRaw) {
      for (let i = 0; i < groupsArrayRaw.length; i++) {
        groupsArray.push(groupsArrayRaw[i]._links.self.href);
      }
    }

    return groupsArray;
  }

  public refreshCurrentUser(): Observable<void> {
    this.logger.info('refreshCurrentUser()');
    return this.storageService.getItemFromLocalStorage('lastUsedTenant', 'getLastUsedTenant()').pipe(
      take(1),
      switchMap((lastUsedTenant: ITenantLite) => {
        return this.getUserResource(lastUsedTenant.links.currentUser).pipe(
          take(1),
          map(() => void 0)
        );
      })
    );
  }

  public getUserResource(currentUserUrl: string): Observable<any> {
    this.logger.info('getUserResource()');
    return this.httpClient.get<any>(currentUserUrl)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        tap((res: any) => {
          this.logger.info('currentUserEmail: ' + res._embedded.user.email);
          this.logger.info('currentUserID: ' + res._embedded.user._links.self.href);
          this.userRaw = res;
          this.userSignal.set(this.deserializeUser(res, false));
        }),
        map((res: any) => this.userSignal()),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('getUserResource() error:' + JSON.stringify(error));
          return throwError(() => error);
        })
      );
  }

  public saveLanguageChange(itemToSave: string, user): Observable<void> {
    this.logger.info('saveLanguageChange()');
    let objectToSend: any = {
      op: 'replace',
      path: '/language',
      value: itemToSave
    };

    return this.httpClient.patch(user.embedded.user.links.self.href, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('saveLanguageChange() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  public resetUser(): Observable<void> {
    this.logger.info('resetUser()');
    return of(this.userSignal.set(null));
  }

  public changePrinterQueuePinStatus(printQueueUrl: string, isPinned: boolean): Observable<void> {
    this.logger.info('changePrinterQueuePinStatus() ' + 'isPinned ' + isPinned);

    if (!isPinned) {
     return this.removePinnedQueue(printQueueUrl);
    } else {
      return this.addPinnedQueue(printQueueUrl);
    }
  }

  private addPinnedQueue(printQueueUrl: string): Observable<void> {
    this.logger.info('addPinnedQueue()');
    let objectToSend: any = {
      op: 'add',
      path: '/userAppSettings/pinnedPrinterQueues/-',
      value: printQueueUrl
    };

    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToSend.path = '/userAppSettings';
      objectToSend.value = {
        pinnedPrinterQueues: [printQueueUrl]
      };
    } else if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
      objectToSend.path = '/userAppSettings/pinnedPrinterQueues';
      objectToSend.value = [printQueueUrl];
    }

    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
              this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('addPinnedQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  private removePinnedQueue(printQueueUrl: string): Observable<void> {
    this.logger.info('removePinnedQueue(): ' + printQueueUrl);
    let objectToSend: any = {};

    let indexOfPrinter = this.userRaw.userAppSettings && this.userRaw.userAppSettings.pinnedPrinterQueues ? this.userRaw.userAppSettings.pinnedPrinterQueues.indexOf(printQueueUrl) : -1;
    if (indexOfPrinter > -1) {
      objectToSend.op = 'remove';
      objectToSend.path = '/userAppSettings/pinnedPrinterQueues/' + indexOfPrinter;
      delete objectToSend.value;

      return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
        .pipe(
          retryWhen(error => error.pipe(
            delay(1000),
            take(3)
          )),
          tap((response) => {
            if (this.userRaw && !this.userRaw.userAppSettings) {
              this.userRaw['userAppSettings'] = {};
              if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
                this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
              }
            }
            this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
            this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          }),
          map(() => void 0),
          catchError((error: HttpErrorResponse) => {
            this.logger.error('removePinnedQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
            return throwError(() => error);
          })
        );
    } else {
      this.logger.info('Queue is not saved in userAppSettings');
      return;
    }
  }

  public setAvailablePinnedPrinterQueues(queueUrls: Array<string>): Observable<void> {
    this.logger.info('setAvailablePinnedPrinterQueues()');

    let objectToPatch: Array<any> = [];

    objectToPatch.push(
      {
        'op': 'remove',
        'path': '/userAppSettings/pinnedPrinterQueues'
      }
    );

    if (queueUrls.length > 0) {
      objectToPatch.push(
        {
          'op': 'add',
          'path': '/userAppSettings/pinnedPrinterQueues/',
          'value': []
        }
      );
      queueUrls.forEach((url) => {
        objectToPatch.push(
          {
            'op': 'add',
            'path': '/userAppSettings/pinnedPrinterQueues/-',
            'value': url
          }
        );
      });
    }

    return this.httpClient.patch(this.userSignal().links.self, objectToPatch)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
              this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setAvailablePinnedPrinterQueues() PATCH ERROR - Object to send: ' + JSON.stringify(objectToPatch), error);
          return throwError(() => error);
        })
      );
  }

  public changePrinterPinStatus(printerUrls: Array<string>, isPinned: boolean): Observable<void> {
    this.logger.info('changePrinterPinStatus() to NO pinned printers');

    let objectToPatch: Array<any> = [];

    printerUrls.forEach(url => {
      objectToPatch.push(
        {
          "op": "remove",
          path: '/userAppSettings/pinnedPrinters/' + 0
        }
      );
    });

    return this.httpClient.patch(this.userSignal().links.self, [objectToPatch])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinters) {
              this.userRaw.userAppSettings['pinnedPrinters'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinters = response['userAppSettings'].pinnedPrinters;
          this.userSignal().userAppSettings.pinnedPrinters = response['userAppSettings'].pinnedPrinters;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('changePrinterPinStatus() PATCH ERROR - Object to send: ' + JSON.stringify(objectToPatch), error);
          return throwError(() => error);
        })
      );
  }

  public setLastUsedPrinter = function (printer: IPrinter): Observable<void> {
    this.logger.info('setLastUsedPrinter()');
    let objectToSend: any = {
      op: 'replace',
      path: '/userAppSettings/lastUsedPrinter',
      value: printer.links.self,
    };
    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToSend = {
        op: 'add',
        path: '/userAppSettings',
        value: {
          lastUsedPrinter: printer.links.self
        }
      };
    } else {
      if (this.userRaw && !this.userRaw.userAppSettings.lastUsedPrinter) {
        objectToSend.op = 'add';
      }
    }

    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.lastUsedPrinter) {
              this.userRaw.userAppSettings['lastUsedPrinter'] = null;
            }
          }
          this.userRaw.userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
          this.userSignal().userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setLastUsedPrinter() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  };

  public setLastUsedQueue = function (queue: IQueue): Observable<void> {
    this.logger.info('setLastUsedQueue()');
    let objectToSend: any = {
      op: 'replace',
      path: '/userAppSettings/lastUsedQueue',
      value: queue.links.self,
    };
    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToSend = {
        op: 'add',
        path: '/userAppSettings',
        value: {
          lastUsedQueue: queue.links.self
        }
      };
    } else {
      if (this.userRaw && !this.userRaw.userAppSettings.lastUsedQueue) {
        objectToSend.op = 'add';
      }
    }
    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.lastUsedQueue) {
              this.userRaw.userAppSettings['lastUsedQueue'] = null;
            }
          }

          this.userRaw.userAppSettings.lastUsedQueue = response['userAppSettings'].lastUsedQueue;
          this.userSignal().userAppSettings.lastUsedQueue = response['userAppSettings'].lastUsedQueue;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setLastUsedQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  };

  public setSecurePrintMethods = function (securePrintMethods: Array<string>): Observable<void> {
    this.logger.info('setSecurePrintMethods()');
    let objectToPatch: any = {};
    console.log('securePrintMethods', securePrintMethods);
    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToPatch = [
        {
          op: 'add',
          path: '/userAppSettings',
          value: {
            securePrintMethods: securePrintMethods
          }
        }
      ];
    } else {
      if (this.userRaw && !this.userRaw.userAppSettings.securePrintMethods) {
        objectToPatch = [
          {
            op: 'add',
            path: '/userAppSettings/securePrintMethods',
            value: securePrintMethods
          }
        ];
      } else {
        objectToPatch = [
          {
            op: 'replace',
            path: '/userAppSettings/securePrintMethods',
            value: securePrintMethods
          }
        ];
      }
    }
    console.log('objectToPatch', objectToPatch);

    return this.httpClient.patch(this.userSignal() && this.userSignal().links.self, objectToPatch)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.securePrintMethods) {
              this.userRaw.userAppSettings['securePrintMethods'] = null;
            }
          }

          this.userRaw.userAppSettings.securePrintMethods = response['userAppSettings'].securePrintMethods;
          this.userSignal().userAppSettings.securePrintMethods = response['userAppSettings'].securePrintMethods;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setSecurePrintMethods() PATCH ERROR - Object to send: ' + JSON.stringify(objectToPatch), error);
          return throwError(() => error);
        })
      );
  };

  public removeLastUsedPrinterAndQueue(): Observable<void> {
    this.logger.info('removeLastPrinterAndQueue()');
    let objectToSend: Array<any> = [];

    objectToSend.push(
      {
        op: 'remove',
        path: '/userAppSettings/lastUsedPrinter'
      },
      {
        op: 'remove',
        path: '/userAppSettings/lastUsedQueue'
      }
    );

    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.lastUsedPrinter) {
              this.userRaw.userAppSettings['lastUsedPrinter'] = null;
            }
          }
          this.userRaw.userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
          this.userSignal().userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('removeLastPrinterAndQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  public removeAllPinnedQueues(): Observable<void> {
    this.logger.info('removeAllPinnedQueues()');
    let objectToSend: Array<any> = [];

    objectToSend.push(
      {
        op: 'remove',
        path: '/userAppSettings/pinnedPrinterQueues'
      }
    );


    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
              this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('removeAllPinnedQueues() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  public getIdCode(user: IUser): Observable<any> {
    this.logger.info('getIdCode()');
    const idCodeUrl = user.links.idCode;

    return this.httpClient.get<any>(idCodeUrl)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  public setIdCode(tenant: ITenant): Observable<any> {
    this.logger.info('setIdCode()');
    let idCodesUrl = tenant.links.idCodes;

    return this.httpClient.post<any>(idCodesUrl, {})
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setIdCode() error:' + JSON.stringify(error));
          return throwError(() => error);
        })
      );
  }

  public deleteIdCode(idCodeResource:any): Observable<any> {
    this.logger.info('deleteIdCode()');

    return this.httpClient.delete<any>(idCodeResource._links.self.href)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('deleteIdCode() error:' + JSON.stringify(error));
          return throwError(() => error);
        })
      );
  }

  public getPinCode(user: IUser): Observable<any> {
    this.logger.info('getPinCode()');
    const pinCodeUrl = user.links.pinCode;
    return this.httpClient.get<any>(pinCodeUrl).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public setPinCode(user: IUser, pinCode): Observable<any> {
    this.logger.info('setPinCode()');
    let pinCodeUrl = user.links.pinCode;
    let postObject = {
      pincode: pinCode
    };
    return this.httpClient.post<any>(pinCodeUrl, postObject)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  public deletePinCode(pinCodeResource:any): Observable<any> {
    this.logger.info('deletePinCode()');
    return this.httpClient.delete<any>(pinCodeResource._links.self.href)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }


  public getCards(user: IUser): Observable<any> {
    this.logger.info('getCards()');
    const cardsUrl = user.links.cards;

    return this.httpClient.get<any>(cardsUrl)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  public deleteCard (card: any): Observable<any> {
    this.logger.info('deleteCard()');
    let cardUrl = card._links.self.href;
    return this.httpClient.delete<any>(cardUrl)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  public registerCard(user: IUser, cardCode: any): Observable<any> {
    this.logger.info('registerCard()');
    let cardUrl = user.links.cards;
    let postObject = {
      registrationCode: cardCode
    };
    return this.httpClient.post<any>(cardUrl, postObject)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('registerCard() error:' + JSON.stringify(error));
          return throwError(() => error);
        })
      );
  }
}